import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { IApiProblemDetails } from '../../models/IApiProblemDetails';
import { IUiErrorMessage } from '../../models/IUiErrorMessage';
import { ProblemDetailsHttpStatus } from '../../models/enum';

/**
 * Maps an `HttpErrorResponse` (typically wrapping a .NET ProblemDetails body)
 * into an `IUiErrorMessage` that describes which i18n key the global
 * interceptor should display.
 *
 * Resolution order:
 *  1. HTTP status `0` ............... → `ERRORS.NETWORK`
 *  2. ProblemDetails `errorCode` .... → `ERRORS.<errorCode>`  (key per code)
 *  3. HTTP status fallbacks ......... → `ERRORS.UNAUTHORIZED` / `FORBIDDEN`
 *                                       / `NOT_FOUND` / `VALIDATION`
 *  4. Anything else ................. → `ERRORS.GENERIC`
 *
 * The interceptor falls back to the API's raw `detail` text when the
 * resolved key is missing from the active translation catalog.
 */
@Injectable({ providedIn: 'root' })
export class ProblemDetailsErrorMapperService {
    private readonly fallbackTitleKey = 'ERROR_TITLE';
    private readonly fallbackMessageKey = 'ERRORS.GENERIC';
    private readonly incompatibleCultureErrorCode = '1904';

    map(error: HttpErrorResponse): IUiErrorMessage {
        const pd = this.extractProblemDetails(error);
        // Use `??` (not `||`) so HTTP status `0` (network failure) is preserved.
        const status = error.status ?? pd?.status;
        const errorCode = this.readErrorCode(pd);
        const traceId = this.readTraceId(pd);
        const detail = this.readDetail(pd);

        if (status === ProblemDetailsHttpStatus.NetworkError) {
            return this.build('ERRORS.NETWORK', status, traceId, errorCode, detail);
        }

        if (errorCode === this.incompatibleCultureErrorCode) {
            const messageParams = this.extractCultureParamsFromDetail(detail);
            return this.build(`ERRORS.${errorCode}`, status, traceId, errorCode, detail, messageParams);
        }

        // The API error code IS the translation key (e.g. 1000 -> 'ERRORS.1000').
        // When the key is missing in the catalog, the interceptor falls back to
        // ProblemDetails.detail, so this branch covers every coded API error.
        if (errorCode) {
            return this.build(`ERRORS.${errorCode}`, status, traceId, errorCode, detail);
        }

        // Status-only fallbacks. Reached only when the API didn't return an
        // error code (e.g. an upstream gateway returning a generic 401/403/404
        // or a model-binding 400 with a `errors` collection).
        switch (status) {
            case ProblemDetailsHttpStatus.Unauthorized:
                return this.build('ERRORS.UNAUTHORIZED', status, traceId, errorCode, detail);
            case ProblemDetailsHttpStatus.Forbidden:
                return this.build('ERRORS.FORBIDDEN', status, traceId, errorCode, detail);
            case ProblemDetailsHttpStatus.NotFound:
                return this.build('ERRORS.NOT_FOUND', status, traceId, errorCode, detail);
            case ProblemDetailsHttpStatus.BadRequest:
                if (pd?.errors && Object.keys(pd.errors).length > 0) {
                    return this.build('ERRORS.VALIDATION', status, traceId, errorCode, detail);
                }
                break;
        }

        return this.build(this.fallbackMessageKey, status, traceId, errorCode, detail);
    }

    private build(
        messageKey: string,
        status?: number,
        traceId?: string,
        errorCode?: string,
        detail?: string,
        messageParams?: Record<string, unknown>
    ): IUiErrorMessage {
        return {
            titleKey: this.fallbackTitleKey,
            messageKey,
            messageParams,
            status,
            traceId,
            errorCode,
            detail
        };
    }

    private extractProblemDetails(error: HttpErrorResponse): IApiProblemDetails | null {
        return error.error && typeof error.error === 'object'
            ? (error.error as IApiProblemDetails)
            : null;
    }

    /**
     * Reads the API error code from a ProblemDetails body. The .NET middleware
     * writes it as a top-level `errorCode` (via `ProblemDetails.Extensions`);
     * the `extensions.errorCode` lookup is kept for tolerance against
     * serializers that nest extensions.
     */
    private readErrorCode(pd: IApiProblemDetails | null): string | undefined {
        if (!pd) {
            return undefined;
        }

        const raw = pd.errorCode ?? pd.extensions?.['errorCode'];

        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            return trimmed.length > 0 ? trimmed : undefined;
        }

        return typeof raw === 'number' ? raw.toString() : undefined;
    }

    private readTraceId(pd: IApiProblemDetails | null): string | undefined {
        const raw = pd?.traceId ?? pd?.extensions?.['traceId'];
        return typeof raw === 'string' ? raw : undefined;
    }

    private readDetail(pd: IApiProblemDetails | null): string | undefined {
        const detail = pd?.detail;
        return typeof detail === 'string' && detail.trim().length > 0 ? detail : undefined;
    }

    private extractCultureParamsFromDetail(detail?: string): Record<string, unknown> | undefined {
        if (!detail) {
            return undefined;
        }

        const firstOpen = detail.indexOf('(');
        const firstClose = detail.indexOf(')');
        const secondOpen = detail.indexOf('(', firstClose);
        const secondClose = detail.indexOf(')', secondOpen);

        if (firstOpen < 0 || firstClose < 0 || secondOpen < 0 || secondClose < 0) {
            return undefined;
        }

        const estimatesCulture = detail.substring(firstOpen + 1, firstClose).trim();
        const applicationCulture = detail.substring(secondOpen + 1, secondClose).trim();

        if (!estimatesCulture || !applicationCulture) {
            return undefined;
        }

        return { estimatesCulture, applicationCulture };
    }
}
